// GA4 conversion events, all pushed onto the Google Tag Manager dataLayer with
// the same shape: an `event` name plus flat parameters. GTM triggers key on
// the event name, so the names here are part of the analytics setup and are
// not to be renamed casually.
//
// Every push happens in response to something the visitor just did (a form
// accepted, a click, a course started), never on a page load, so reloading
// a page cannot send an event twice.

interface DataLayerPayload {
  event: string;
  [key: string]: unknown;
}

export const pushDataLayer = (payload: DataLayerPayload) => {
  const globalWindow = window as any;
  globalWindow.dataLayer = globalWindow.dataLayer || [];
  globalWindow.dataLayer.push(payload);
};

interface EnquiryConversionParams {
  event: "team_enquiry" | "workshop_enquiry";
  formLocation: "teams_quote" | "brand_page" | "workshops_enquiry";
  teamSize?: string | number;
}

// An enquiry form accepted by the server: a team sales enquiry or a workshop
// enquiry. These used to be pushed as "purchase", which is GA4's reserved
// name for money changing hands; real sales now send that (see
// pushServerDataLayer below). Only call this after the server has accepted
// the form (i.e. after the reCAPTCHA has been verified) so bot and failed
// submissions are never counted.
export const trackEnquiryConversion = ({
  event,
  formLocation,
  teamSize,
}: EnquiryConversionParams) => {
  pushDataLayer({
    event,
    form_location: formLocation,
    team_size: teamSize,
  });
};

// The click that downloads the team brochure on the For Teams page.
export const trackTeamBrochureDownload = ({
  formLocation,
}: {
  formLocation: "teams_quote";
}) => {
  pushDataLayer({
    event: "teams_brochure_download",
    form_location: formLocation,
  });
};

// The course brochure was downloaded from a course page's confirmation box.
// `newsletter` says whether they joined Weekend Reads in the same click.
export const trackCourseBrochureDownload = ({
  courseSlug,
  newsletter,
}: {
  courseSlug: string;
  newsletter: boolean;
}) => {
  pushDataLayer({
    event: "course_brochure_download",
    course_slug: courseSlug,
    newsletter,
  });
};

// Someone joined an email list: the form was accepted by the server. `list`
// says which one, so each list can be counted on its own in GA4. "general" is
// Weekend Reads and Weekday Wisdom.
export const trackNewsletterSignup = ({ list }: { list: "rory_weekly" | "general" }) => {
  pushDataLayer({
    event: "newsletter_signup",
    list,
  });
};

// Someone described their situation and was handed a matching quote.
export const trackQuoteMatch = ({ tool }: { tool: "rory" }) => {
  pushDataLayer({
    event: "quote_match",
    tool,
  });
};

// A tag the server built for the page to push, such as "sample_course_start"
// returned when a free sample course is started. Absent when there is
// nothing to say.
export const pushServerDataLayer = (payload?: DataLayerPayload | null) => {
  if (payload && payload.event) pushDataLayer(payload);
};
